import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A horizontal scroll-snap product media slider with dots, desktop arrows,
 * and per-slide video autoplay. Listens for variant updates and replaces
 * itself with the freshly-rendered HTML from the server.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends Component<Refs>
 */
export class ProductSlider extends Component {
  requiredRefs = ['track'];

  #controller = new AbortController();
  #visibilityObserver = null;
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    const scopeTarget = this.closest('.shopify-section') ?? document;
    scopeTarget.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    const slides = this.refs.slides ?? [];
    this.#visibilityObserver = new IntersectionObserver(this.#handleIntersection, {
      root: this.refs.track,
      threshold: 0.6,
    });
    for (const slide of slides) this.#visibilityObserver.observe(slide);

    this.refs.track.addEventListener('scroll', this.#handleTrackScroll, { signal, passive: true });

    const dots = this.refs.dots ?? [];
    for (const dot of dots) {
      dot.addEventListener('click', this.#handleDotClick, { signal });
    }

    this.refs.prevButton?.addEventListener('click', () => this.#scrollBy(-1), { signal });
    this.refs.nextButton?.addEventListener('click', () => this.#scrollBy(1), { signal });

    this.#updateActive(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#visibilityObserver?.disconnect();
  }

  #handleVariantUpdate = (event) => {
    const source = event?.detail?.data?.html;
    if (!source) return;
    const next = source.querySelector('product-slider');
    if (!next) return;
    this.replaceWith(next);
  };

  #handleIntersection = (entries) => {
    for (const entry of entries) {
      const video = entry.target.querySelector('video');
      if (!video) continue;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      } else {
        video.pause();
      }
    }
  };

  #handleTrackScroll = () => {
    const track = this.refs.track;
    if (!track) return;
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;
    const slideWidth = track.clientWidth;
    if (slideWidth === 0) return;
    const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / slideWidth)));
    if (index !== this.#activeIndex) this.#updateActive(index);
  };

  #handleDotClick = (event) => {
    const button = event.currentTarget;
    const index = Number(button.dataset.slideIndex);
    if (Number.isNaN(index)) return;
    this.#scrollTo(index);
  };

  #scrollBy(delta) {
    const slides = this.refs.slides ?? [];
    const next = Math.max(0, Math.min(slides.length - 1, this.#activeIndex + delta));
    this.#scrollTo(next);
  }

  #scrollTo(index) {
    const slides = this.refs.slides ?? [];
    const slide = slides[index];
    if (!slide || !this.refs.track) return;
    this.refs.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  #updateActive(index) {
    this.#activeIndex = index;
    const slides = this.refs.slides ?? [];
    const dots = this.refs.dots ?? [];

    for (let i = 0; i < dots.length; i++) {
      const isActive = i === index;
      dots[i].setAttribute('aria-current', isActive ? 'true' : 'false');
    }

    if (this.refs.prevButton) this.refs.prevButton.disabled = index <= 0;
    if (this.refs.nextButton) this.refs.nextButton.disabled = index >= slides.length - 1;
  }
}

if (!customElements.get('product-slider')) {
  customElements.define('product-slider', ProductSlider);
}
